import { useEffect, useMemo, useState } from "react"
import { Localized, useLocalization } from "@fluent/react"
import { alpha, Box, Button, Divider, Drawer, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select, Stack, TextField, Tooltip, Typography } from "@mui/material"
import { amber, blue, deepPurple, grey, orange, pink, red, teal } from "@mui/material/colors"
import DOMPurify from "dompurify"
import CloseIcon from "@mui/icons-material/Close"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import SearchIcon from "@mui/icons-material/Search"
import { NoteRichTextEditor } from "./NoteRichTextEditor"
import { NoteColor, UserNote, useUserNotes } from "./UserNotesContext"

const isNoteEmpty = (html: string) => {
    const body = new DOMParser().parseFromString(html, 'text/html').body
    return !body.textContent?.trim() && !body.querySelector('img, table')
}

const noteColors: Record<NoteColor, string> = {
    GREEN: teal[500],
    YELLOW: amber[600],
    BLUE: blue[500],
    PINK: pink[400],
    ORANGE: orange[600],
    PURPLE: deepPurple[400],
    RED: red[500],
    GRAY: grey[600],
}

const colorNames: Record<NoteColor, string> = {
    GREEN: "_Green",
    YELLOW: "_Yellow",
    BLUE: "_Blue",
    PINK: "_Pink",
    ORANGE: "_Orange",
    PURPLE: "_Purple",
    RED: "_Red",
    GRAY: "_Gray",
}

const NoteColorPicker = ({ value, onChange }: { value: NoteColor, onChange: (color: NoteColor) => void }) => {
    const { l10n } = useLocalization()
    return <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
        <Typography variant="caption"><Localized id="user-notes-color">_Color</Localized></Typography>
        {(Object.keys(noteColors) as NoteColor[]).map(color => {
            const label = l10n.getString(`user-notes-color-${color.toLowerCase()}`, null, colorNames[color])
            return <Tooltip key={color} title={label}>
                <IconButton size="small" onClick={() => onChange(color)} aria-label={label}
                    aria-pressed={value === color}
                    sx={{ width: 30, height: 30, border: 2, borderColor: value === color ? 'text.primary' : 'divider' }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: noteColors[color] }} />
                </IconButton>
            </Tooltip>
        })}
    </Stack>
}

export const UserNotesPanel = ({ onNavigate }: {
    onNavigate: (note: UserNote) => void
}) => {
    const { open, target, notes, closeNotes, addNote, updateNote, deleteNote } = useUserNotes()
    const { l10n } = useLocalization()
    const [content, setContent] = useState("")
    const [createEditorKey, setCreateEditorKey] = useState(0)
    const [contentEmpty, setContentEmpty] = useState(true)
    const [color, setColor] = useState<NoteColor>("GREEN")
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
    const [editedContent, setEditedContent] = useState("")
    const [editedContentEmpty, setEditedContentEmpty] = useState(true)
    const [editedColor, setEditedColor] = useState<NoteColor>("GREEN")
    const [adding, setAdding] = useState(false)
    const [query, setQuery] = useState("")
    const [groupFilter, setGroupFilter] = useState("")
    const availableTypes = useMemo(() => Array.from(new Set(notes.map(note => note.objectType))), [notes])
    useEffect(() => {
        if (groupFilter && !availableTypes.includes(groupFilter)) setGroupFilter("")
    }, [availableTypes, groupFilter])
    const visibleNotes = useMemo(() => {
        if (target) {
            return notes.filter(note => note.objectType === target.objectType && note.objectGuid === target.objectGuid)
        }
        const selectedNotes = !groupFilter
            ? notes
            : notes.filter(note => note.objectType === groupFilter)
        const search = query.trim().replace(/\s+/g, ' ').toLocaleLowerCase()
        if (!search) return selectedNotes
        const parser = new DOMParser()
        return selectedNotes.filter(note => {
            const body = parser.parseFromString(note.content, 'text/html').body
            body.querySelectorAll('br').forEach(element => element.replaceWith(' '))
            body.querySelectorAll('p, div, li').forEach(element => element.append(' '))
            const text = body.textContent ?? ''
            return `${note.objectLabel} ${text}`.replace(/\s+/g, ' ').toLocaleLowerCase().includes(search)
        })
    }, [notes, target, query, groupFilter])
    const noteGroups = useMemo(() => {
        if (target) return [{ type: target.objectType, items: visibleNotes }]
        const types = ["STRUCTURE", "REQUIREMENT",
            ...new Set(visibleNotes.map(note => note.objectType)
                .filter(type => type !== "STRUCTURE" && type !== "REQUIREMENT"))]
        return types.map(type => ({
            type,
            items: visibleNotes.filter(note => note.objectType === type),
        })).filter(group => group.items.length > 0)
    }, [target, visibleNotes])
    const showComposer = target !== null && (visibleNotes.length === 0 || adding)

    useEffect(() => {
        if (!open) {
            setContent("")
            setCreateEditorKey(current => current + 1)
            setContentEmpty(true)
            setColor("GREEN")
            setEditingNoteId(null)
            setEditedContent("")
            setEditedContentEmpty(true)
            setEditedColor("GREEN")
            setAdding(false)
            setQuery("")
        }
    }, [open])

    const handleClose = () => {
        setContent("")
        setCreateEditorKey(current => current + 1)
        setContentEmpty(true)
        setColor("GREEN")
        setEditingNoteId(null)
        setEditedContent("")
        setEditedContentEmpty(true)
        setEditedColor("GREEN")
        setAdding(false)
        setQuery("")
        closeNotes()
    }

    const handleAdd = () => {
        if (!target || contentEmpty || content.length > 4000) return
        addNote(target, content, color)
        setContent("")
        setCreateEditorKey(current => current + 1)
        setContentEmpty(true)
        setColor("GREEN")
        setAdding(false)
    }

    const handleSave = (note: UserNote) => {
        if (editedContentEmpty || editedContent.length > 4000 || (editedContent === note.content && editedColor === note.color)) return
        updateNote(note.id, editedContent, editedColor)
        setEditingNoteId(null)
        setEditedContent("")
        setEditedContentEmpty(true)
        setEditedColor("GREEN")
    }

    return <Drawer anchor="right" open={open} onClose={handleClose}>
        <Box sx={{ width: { xs: '100vw', sm: 420 }, maxWidth: '100vw', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" alignItems="center" sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                    <Localized id="notes">_Notes</Localized> ({visibleNotes.length})
                </Typography>
                <IconButton onClick={handleClose} aria-label={l10n.getString("close", null, "_Close")}>
                    <CloseIcon />
                </IconButton>
            </Stack>
            <Divider />
            {!target && <Stack spacing={2} sx={{ p: 2 }}>
                <FormControl size="small" fullWidth>
                    <InputLabel id="user-notes-type-filter-label">
                        <Localized id="user-notes-group-filter">_Note type</Localized>
                    </InputLabel>
                    <Select labelId="user-notes-type-filter-label" value={groupFilter}
                        label={l10n.getString("user-notes-group-filter", null, "_Note type")}
                        onChange={event => setGroupFilter(event.target.value)}>
                        <MenuItem value=""><Localized id="user-notes-filter-all">_All</Localized></MenuItem>
                        {availableTypes.map(type => <MenuItem key={type} value={type}>
                            {type === "STRUCTURE"
                                ? <Localized id="user-notes-group-structures">_Structures</Localized>
                                : type === "REQUIREMENT"
                                    ? <Localized id="user-notes-group-requirements">_Requirements</Localized>
                                    : type}
                        </MenuItem>)}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    fullWidth
                    label={<Localized id="user-notes-search">_Search notes</Localized>}
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                        endAdornment: query && <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setQuery("")}
                                aria-label={l10n.getString("clear", null, "_Clear")}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>,
                    }}
                />
            </Stack>}
            {target && <Box sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2" sx={{ flex: 1, overflowWrap: 'anywhere' }}>
                        {target.label}
                    </Typography>
                    {!showComposer && <Button size="small" onClick={() => setAdding(true)}>
                        <Localized id="user-notes-add">_Add note</Localized>
                    </Button>}
                </Stack>
                {showComposer && <Paper variant="outlined" sx={{
                    mt: 1.5, p: 1.5, border: 2,
                    borderColor: noteColors[color],
                    bgcolor: alpha(noteColors[color], 0.12),
                }}>
                    <Stack spacing={1.5}>
                        <NoteRichTextEditor
                            key={createEditorKey}
                            value={content}
                            onValueChange={html => {
                                setContent(html)
                                setContentEmpty(isNoteEmpty(html))
                            }}
                        />
                        {content.length > 4000 && <Typography variant="caption" color="error">
                            <Localized id="user-notes-too-long">_Note cannot exceed 4000 characters.</Localized>
                        </Typography>}
                        <NoteColorPicker value={color} onChange={setColor} />
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" size="small" onClick={handleAdd} disabled={contentEmpty || content.length > 4000}>
                                <Localized id="user-notes-add">_Add note</Localized>
                            </Button>
                            {visibleNotes.length > 0 && <Button size="small" onClick={() => {
                                setAdding(false)
                                setContent("")
                                setCreateEditorKey(current => current + 1)
                                setContentEmpty(true)
                                setColor("GREEN")
                            }}>
                                <Localized id="cancel">_Cancel</Localized>
                            </Button>}
                        </Stack>
                    </Stack>
                </Paper>}
            </Box>}
            {target && <Divider />}
            <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
                {visibleNotes.length === 0
                    ? <Typography color="text.secondary">
                        {!target && (query.trim() || (groupFilter !== "" && notes.length > 0))
                            ? <Localized id="user-notes-no-results">_No matching notes.</Localized>
                            : <Localized id="user-notes-empty">_No notes yet.</Localized>}
                    </Typography>
                    : <Stack spacing={2.5}>
                        {noteGroups.map(group => <Box component="section" key={group.type}>
                            {!target && <Typography component="h3" variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                                {group.type === "STRUCTURE"
                                    ? <Localized id="user-notes-group-structures">_Structures</Localized>
                                    : group.type === "REQUIREMENT"
                                        ? <Localized id="user-notes-group-requirements">_Requirements</Localized>
                                        : group.type}
                                {` (${group.items.length})`}
                            </Typography>}
                            <Stack spacing={1.5}>
                        {group.items.map(note => <Paper key={note.id} variant="outlined" sx={{
                            border: 2,
                            borderColor: noteColors[note.color],
                            bgcolor: 'background.paper',
                            overflow: 'hidden',
                        }}>
                            <Stack direction="row" alignItems="center" spacing={0.5}
                                sx={{ px: 1, py: 0.5, bgcolor: alpha(noteColors[note.color], 0.16) }}>
                                <Typography variant="caption" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', fontWeight: 600 }}>
                                        {note.objectType === "STRUCTURE"
                                            ? <Localized id="menu-structure">_Structure</Localized>
                                            : note.objectType === "REQUIREMENT"
                                                ? <Localized id="user-notes-object-requirement">_Requirement</Localized>
                                                : note.objectType}
                                        {`: ${note.objectLabel}`}
                                </Typography>
                                <Tooltip title={<Localized id="user-notes-open">_Go to object</Localized>}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={!note.objectGuid}
                                            onClick={() => onNavigate(note)}
                                            aria-label={`${l10n.getString("user-notes-open", null, "_Go to object")}: ${note.objectLabel}`}
                                        >
                                            <ArrowForwardIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                {editingNoteId !== note.id && <IconButton
                                    size="small"
                                    onClick={() => {
                                        setEditingNoteId(note.id)
                                        setEditedContent(note.content)
                                        setEditedContentEmpty(false)
                                        setEditedColor(note.color)
                                    }}
                                    aria-label={l10n.getString("notes-edit", null, "_Edit note")}
                                >
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>}
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        deleteNote(note.id)
                                        if (editingNoteId === note.id) {
                                            setEditingNoteId(null)
                                            setEditedContent("")
                                            setEditedContentEmpty(true)
                                            setEditedColor("GREEN")
                                        }
                                    }}
                                    aria-label={l10n.getString("user-notes-delete", null, "_Delete note")}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                            <Box sx={{ p: 1.5 }}>
                                {editingNoteId === note.id
                                    ? <Stack spacing={1}>
                                        <NoteRichTextEditor
                                            value={editedContent}
                                            
                                            onValueChange={html => {
                                                setEditedContent(html)
                                                setEditedContentEmpty(isNoteEmpty(html))
                                            }}
                                        />
                                        {editedContent.length > 4000 && <Typography variant="caption" color="error">
                                            <Localized id="user-notes-too-long">_Note cannot exceed 4000 characters.</Localized>
                                        </Typography>}
                                        <NoteColorPicker value={editedColor} onChange={setEditedColor} />
                                        <Stack direction="row" spacing={1}>
                                            <Button size="small" variant="contained" onClick={() => handleSave(note)}
                                                disabled={editedContentEmpty || editedContent.length > 4000 || (editedContent === note.content && editedColor === note.color)}>
                                                <Localized id="save">_Save</Localized>
                                            </Button>
                                            <Button size="small" onClick={() => {
                                                setEditingNoteId(null)
                                                setEditedContent("")
                                                setEditedContentEmpty(true)
                                                setEditedColor("GREEN")
                                            }}>
                                                <Localized id="cancel">_Cancel</Localized>
                                            </Button>
                                        </Stack>
                                    </Stack>
                                    : <Box className="tiptap" sx={{
                                        whiteSpace: 'pre-wrap',
                                        overflowWrap: 'anywhere',
                                        '& img, & table': { maxWidth: '100%' },
                                    }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.content) }} />}
                            </Box>
                        </Paper>)}
                            </Stack>
                        </Box>)}
                    </Stack>}
            </Box>
        </Box>
    </Drawer>
}
